/**
 * Hospital letterhead — Storage path letterheads/{orgId}/{fileName}
 * Field on organizations/{orgId}: letterheadImageUrl
 */

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import { storage, db } from '../firebase';
import { organizationService } from './firebaseService';

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg']);

function sanitizeFileName(name) {
  const base = (name || 'letterhead').split(/[/\\]/).pop();
  return base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120) || 'letterhead';
}

/**
 * Extract Storage object path from a Firebase download URL (v0 API).
 * @param {string} url
 * @returns {string|null}
 */
export function storagePathFromDownloadURL(url) {
  if (!url || typeof url !== 'string') return null;
  try {
    const marker = '/o/';
    const idx = url.indexOf(marker);
    if (idx === -1) return null;
    const rest = url.slice(idx + marker.length);
    const pathPart = rest.split('?')[0];
    return decodeURIComponent(pathPart);
  } catch {
    return null;
  }
}

async function deleteObjectByDownloadURL(url) {
  const path = storagePathFromDownloadURL(url);
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (e) {
    console.warn('[letterheadService] Could not delete previous letterhead file:', e?.message || e);
  }
}

/**
 * @param {string} orgId
 * @param {File} file
 * @returns {Promise<string>} download URL
 */
export async function uploadLetterhead(orgId, file) {
  if (!orgId || !file) throw new Error('orgId and file are required');
  if (!ALLOWED_TYPES.has((file.type || '').toLowerCase())) {
    throw new Error('INVALID_TYPE');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('FILE_TOO_LARGE');
  }

  const orgSnap = await getDoc(doc(db, 'organizations', orgId));
  if (!orgSnap.exists()) throw new Error('ORG_NOT_FOUND');

  const prevUrl = orgSnap.data()?.letterheadImageUrl;
  if (prevUrl) {
    await deleteObjectByDownloadURL(prevUrl);
  }

  const safeName = sanitizeFileName(file.name);
  const storagePath = `letterheads/${orgId}/${Date.now()}_${safeName}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, {
    contentType: file.type || 'image/jpeg'
  });
  const downloadUrl = await getDownloadURL(storageRef);

  await organizationService.update(orgId, { letterheadImageUrl: downloadUrl });
  return downloadUrl;
}

/**
 * @param {string} orgId
 * @returns {Promise<void>}
 */
export async function deleteLetterhead(orgId) {
  if (!orgId) return;
  const orgSnap = await getDoc(doc(db, 'organizations', orgId));
  if (!orgSnap.exists()) return;

  const prevUrl = orgSnap.data()?.letterheadImageUrl;
  if (prevUrl) {
    await deleteObjectByDownloadURL(prevUrl);
  }
  await organizationService.update(orgId, { letterheadImageUrl: null });
}

/**
 * @param {string} orgId
 * @returns {Promise<string|null>}
 */
export async function getLetterheadUrl(orgId) {
  if (!orgId) return null;
  const orgSnap = await getDoc(doc(db, 'organizations', orgId));
  if (!orgSnap.exists()) return null;
  const url = orgSnap.data()?.letterheadImageUrl;
  return url && typeof url === 'string' ? url : null;
}
